/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { addCustomField } from 'store/slices/shopSlice'
import { CustomFieldGroup } from 'models/shop'
import { snakeCase } from 'helpers/utils'
import { useAppDispatch } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    productID: number,
    customFieldGroup: CustomFieldGroup,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CustomFieldsForm({ productID, customFieldGroup, close }: Props) {
    const dispatch = useAppDispatch()
    const { custom_field_inputs } = customFieldGroup
    const initialValues = Object.assign({}, ...custom_field_inputs.map(({ field_name, field_type, field_options }) => {
        return {
            field_name: snakeCase(field_name),
            field_type,
            field_options
        }
    }).map(({ field_name, field_type, field_options }) => {
        if (field_type === 'dropdown') {
            return {
                [field_name]: field_options.split(',')[0] || ''
            }
        }

        if (field_type === 'checkbox') {
            return {
                [field_name]: false
            }
        }

        return {
            [field_name]: ''
        }
    }))
    const schema = Object.assign({}, ...custom_field_inputs.map(({ field_name, field_type, required }) => {
        return {
            field_name: snakeCase(field_name),
            field_type,
            required
        }
    }).map(({ field_name, field_type, required }) => {
        if (field_type === 'checkbox') {
            return {
                [field_name]: Yup.bool()
            }
        }

        if (required) {
            return {
                [field_name]: Yup.string().required('Required')
            }
        }

        return {
            [field_name]: Yup.string()
        }
    }))

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={
                Yup.object(schema)
            }
            onSubmit={(values) => {
                dispatch(addCustomField({
                    product_id: productID,
                    custom_fields: custom_field_inputs.map(({ id, field_name }) => {
                        return {
                            input_id: id,
                            value: values[snakeCase(field_name)]
                        }
                    })
                }))
                close()
            }}
        >
            {({ isSubmitting }) => (
                <Form id={'customFieldsForm'}>
                    {custom_field_inputs &&
                        custom_field_inputs.map(({ field_name, field_type, field_options }, index) => {
                            if (field_type === 'dropdown') {
                                return (
                                    <Field
                                        label={field_name}
                                        name={snakeCase(field_name)}
                                        key={`${snakeCase(field_name)}_${index}`}
                                        type={'select'}
                                        list={field_options.split(',').map((option) => ({
                                            label: option,
                                            value: option
                                        }))}
                                        disabled={isSubmitting}
                                        component={NXUIForm.SelectField}
                                    />
                                )
                            }

                            if (field_type === 'text') {
                                return (
                                    <Field
                                        label={field_name}
                                        name={snakeCase(field_name)}
                                        key={`${snakeCase(field_name)}_${index}`}
                                        type={'text'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.InputField}
                                    />
                                )
                            }

                            if (field_type === 'textarea') {
                                return (
                                    <Field
                                        label={field_name}
                                        name={snakeCase(field_name)}
                                        key={`${snakeCase(field_name)}_${index}`}
                                        type={'text'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.TextAreaField}
                                    />
                                )
                            }

                            if (field_type === 'password') {
                                return (
                                    <Field
                                        label={field_name}
                                        name={snakeCase(field_name)}
                                        key={`${snakeCase(field_name)}_${index}`}
                                        type={'password'}
                                        autoComplete={'current-password'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.InputField}
                                    />
                                )
                            }

                            if (field_type === 'checkbox') {
                                return (
                                    <Field
                                        label={field_name}
                                        name={snakeCase(field_name)}
                                        key={`${snakeCase(field_name)}_${index}`}
                                        type={'checkbox'}
                                        component={NXUIForm.CheckboxField}
                                    />
                                )
                            }

                            return null
                        })
                    }
                </Form>
            )}
        </Formik>
    )
}
